<script setup>
  import { Switch, SwitchGroup, SwitchLabel, Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/vue'
  import { ErrorMessage, Field, useField } from 'vee-validate'
  import { unaccent } from '~/utils/string'

  const props = defineProps({
    accept: { type: String },
    as: { default: 'div', type: String },
    autocomplete: { type: String },
    autoResize: { default: false, type: Boolean },
    custom: { default: false, type: Boolean },
    disabled: { type: Boolean },
    error: { type: String },
    id: { type: String },
    imageClass: { default: 'h-28 w-28 rounded-full', type: String },
    inputClass: { type: [String, Object] },
    comboboxClass: { default: () => ({}), type: Object },
    label: { type: String },
    switchLabelLeft: { type: String },
    max: { type: String },
    maxlength: { type: [String, Number] },
    min: { type: String },
    multiple: { type: Boolean },
    name: { type: String },
    options: { type: Array },
    placeholder: { type: String },
    readonly: { type: Boolean },
    required: { type: Boolean },
    rows: { type: String },
    type: { default: 'text', type: String },
    uncheckedValue: { type: undefined },
    value: { type: undefined },
    noTruncateOptions: { default: false, type: Boolean },
    withAutocomplete: { default: false, type: Boolean },
    remoteSearch: { default: false, type: Boolean },
    checkboxListClass: { default: 'space-y-2', type: String },
    showAllSelectedValues: { default: true, type: Boolean },
    // Show the multi-value hint only where explicitly requested.
    showAddHint: { default: false, type: Boolean }
  })
  const emit = defineEmits(['change', 'search', 'select', 'switch'])

  const theme = useCareerTheme()
  const valueTagStyle = computed(() => (theme.value.primary ? { backgroundColor: theme.value.primary } : undefined))
  const valueTagClass = computed(() => [
    'inline-flex max-w-full cursor-pointer items-center space-x-1 rounded-sm px-1 py-0.5 text-sm duration-300',
    theme.value.primary ? 'text-white hover:brightness-95' : 'bg-viking-400 text-tarawera-900 hover:bg-viking-500'
  ])

  const selectedValue = ref(props.value)
  const selectedValues = ref(Array.isArray(props.value) ? props.value : props.value ? [props.value] : [])
  const switchValue = ref(!!props.value)

  const showPassword = ref(false)

  const field = useField(props.name, undefined)
  const imageInput = ref(null)
  const imageUrl = ref(props.type === 'image' && props.value?.url ? useStrapiMedia(props.value.url) : null)
  const textareaRef = ref(null)

  const autocomplete = {
    inputRef: ref(null),
    optionsRef: ref(null),
    query: ref(''),
    // Hide the hint while the input cursor is visible.
    focused: ref(false),
    filteredOptions: computed(() => {
      if (!props.options) return []
      if (props.remoteSearch || !autocomplete.query.value) return props.options

      const q = unaccent(autocomplete.query.value.toLowerCase())
      return props.options
        .map(option => {
          const labelIndex = unaccent(String(option.label ?? '').toLowerCase()).indexOf(q)
          const valueIndex = unaccent(String(option.value ?? '').toLowerCase()).indexOf(q)
          const indexes = [labelIndex, valueIndex].filter(index => index !== -1)

          return {
            option,
            matchIndex: indexes.length ? Math.min(...indexes) : Infinity
          }
        })
        .filter(result => result.matchIndex !== Infinity)
        .sort((a, b) => a.matchIndex - b.matchIndex)
        .map(result => result.option)
    }),
    focus: () => {
      if (props.disabled) return
      autocomplete.inputRef.value?.$el?.focus()
    },
    getActiveOption: () => {
      if (!autocomplete.optionsRef.value) return null

      const optionsContainer = autocomplete.optionsRef.value.$el || autocomplete.optionsRef.value
      if (!optionsContainer) return null

      let activeElement = optionsContainer.querySelector('[data-headlessui-state*="active"]')
      if (!activeElement) activeElement = optionsContainer.querySelector('.bg-zinc-100')
      if (!activeElement) return null

      const optionElement = activeElement.closest('[role="option"]') || activeElement.parentElement || activeElement
      const activeText = optionElement.textContent?.trim().replace(/\s*\(max atteint\)\s*$/, '')
      if (!activeText) return null

      return (
        autocomplete.filteredOptions.value.find(option => {
          const optionLabel = option.label?.trim() || ''
          return optionLabel === activeText || optionLabel === activeText.trim()
        }) || null
      )
    },
    confirmSelection: () => {
      if (props.multiple) {
        if (!Array.isArray(selectedValues.value)) return

        const max = props.maxlength ? Number.parseInt(props.maxlength) : null
        if (max && selectedValues.value.length >= max) return
      }

      const activeOption = autocomplete.getActiveOption()
      const optionToSelect =
        activeOption && (!selectedValues.value.includes(activeOption.value) || selectedValue.value !== activeOption.value)
          ? activeOption
          : autocomplete.filteredOptions.value.find(option => !selectedValues.value.includes(option.value))
      if (!optionToSelect) return

      autocomplete.query.value = ''
      if (props.multiple) {
        selectedValues.value = [...selectedValues.value, optionToSelect.value]
        nextTick(() => autocomplete.focus())
      } else {
        selectedValue.value = optionToSelect.value
      }
    },
    handleBackspaceKey: event => {
      if (autocomplete.query.value) return
      event.preventDefault()
      if (props.multiple) {
        if (!Array.isArray(selectedValues.value) || !selectedValues.value.length) return
        event.preventDefault()
        selectedValues.value = selectedValues.value.slice(0, -1)
      } else {
        if (!selectedValue.value) return
        autocomplete.query.value = ''
        selectedValue.value = ''
        if (event.target?.value) event.target.value = ''
      }
    },
    clearQuery: () => {
      autocomplete.query.value = ''
      if (!props.multiple) return
      const el = autocomplete.inputRef.value?.$el
      if (el && 'value' in el && el.value) el.value = ''
    },
    handleEnterKey: event => {
      if (props.multiple) event.preventDefault()
      if (!autocomplete.query.value) return
      event.preventDefault()
      autocomplete.confirmSelection()
    },
    handleInput: e => {
      autocomplete.query.value = e.target.value
      if (!autocomplete.query.value && !props.multiple) selectedValue.value = ''
      emit('search', autocomplete.query.value)
    }
  }

  const resizeTextarea = async () => {
    if (!props.autoResize || !textareaRef.value) return
    textareaRef.value.style.height = 'auto'
    await nextTick()
    textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`
  }

  const handleImageChange = event => {
    const file = event.target.files[0]
    if (!props.accept.includes(file.type)) return
    field.handleChange(file)

    const reader = new FileReader()
    reader.onload = e => (imageUrl.value = e.target.result)
    reader.readAsDataURL(file)
  }

  const clearImageFile = () => {
    field.handleReset()
    field.setValue(null)
    imageInput.value.value = null
    imageUrl.value = null
  }

  const handleCheckboxListChange = (value, checked) => {
    selectedValues.value = checked ? [...selectedValues.value, value] : selectedValues.value.filter(v => v !== value)
    field.setValue(selectedValues.value)
  }

  watch(
    () => props.value,
    newValue => {
      if (!props.multiple) selectedValue.value = newValue
      if (props.multiple || props.type === 'checkbox-list') selectedValues.value = newValue || []
      if (props.type === 'switch') switchValue.value = !!newValue
    },
    { immediate: true }
  )

  watch(selectedValue, newSelectedValue => {
    emit('select', newSelectedValue)
    field.setTouched(true)
    if (props.withAutocomplete) autocomplete.clearQuery()
  })

  watch(selectedValues, newSelectedValues => {
    emit('select', newSelectedValues)
    field.setTouched(true)
    if (props.withAutocomplete) autocomplete.clearQuery()
  })

  watch(switchValue, newSwitchValue => {
    emit('switch', newSwitchValue)
    field.setTouched(true)
  })
</script>

<template>
  <component :is="props.as">
    <template v-if="!['checkbox', 'image', 'radio', 'switch', 'checkbox-list'].includes(props.type)">
      <label class="mb-1 block font-bold" v-if="props.label" :for="props.id">
        {{ props.label }}<span class="ml-0.5 text-zinc-400" v-if="props.required">*</span>
      </label>
      <slot v-else name="label" />
    </template>

    <template v-if="props.type === 'select'">
      <Field v-if="!props.custom && !props.multiple" v-slot="{ field, value }" :name="props.name">
        <select
          class="form-select w-full min-w-0 truncate"
          v-bind="field"
          :class="{
            error: !!props.error,
            '!text-zinc-400': !value
          }"
          :disabled="props.disabled"
          :id="props.id"
          :required="props.required"
          :readonly="props.readonly"
          @change="$event => emit('select', $event.target.value)"
        >
          <slot />
          <template v-if="props.options">
            <option v-if="props.placeholder" value="">{{ props.placeholder }}</option>
            <option v-for="option in props.options" :key="option.value" :selected="option.selected || option.value === value" :value="option.value">
              {{ option.label }}
            </option>
          </template>
        </select>
      </Field>

      <Field v-else-if="!props.multiple" v-model="selectedValue" :name="props.name">
        <Combobox class="relative" v-model="selectedValue" :id="props.id" as="div" :disabled="props.disabled">
          <!-- Listbox behavior with simple select-->
          <template v-if="!props.withAutocomplete">
            <ComboboxButton
              as="button"
              :disabled="props.disabled || props.readonly"
              :class="[
                'form-select w-full min-w-0',
                props.comboboxClass.button,
                {
                  error: !!props.error,
                  '!text-zinc-400': !selectedValue,
                  '!bg-zinc-100 !opacity-100': props.readonly
                }
              ]"
            >
              {{ !selectedValue ? props.placeholder : options?.find(o => o.value === selectedValue)?.label }}
            </ComboboxButton>
          </template>
          <!-- Combobox behavior with simple select-->
          <template v-else>
            <div class="relative w-full">
              <ComboboxInput
                :class="['form-select w-full min-w-0', { error: !!props.error }]"
                :ref="autocomplete.inputRef"
                :disabled="props.disabled"
                :display-value="val => props.options?.find(o => o.value === val)?.label ?? ''"
                :placeholder="props.placeholder"
                @input="autocomplete.handleInput"
                @keydown.backspace.capture="autocomplete.handleBackspaceKey"
                @keydown.enter.capture="autocomplete.handleEnterKey"
                @keydown.tab.capture.prevent="autocomplete.handleEnterKey"
              />
              <ComboboxButton
                as="button"
                :class="['absolute inset-y-0 right-0 flex w-full items-center text-zinc-500', props.comboboxClass.button]"
              ></ComboboxButton>
            </div>
          </template>
          <!-- Options for simple select -->
          <ComboboxOptions
            :class="[
              'absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow outline-none focus:border-viking-400 focus-visible:ring-2 focus-visible:ring-viking-400/50',
              props.comboboxClass.options
            ]"
            :ref="autocomplete.optionsRef"
          >
            <slot />
            <template v-if="props.options">
              <!-- clear option -->
              <ComboboxOption
                v-if="props.placeholder && autocomplete.query.value === '' && selectedValue"
                :value="''"
                :class="[
                  'cursor-default px-2 py-1 text-zinc-500 hover:bg-zinc-100',
                  props.comboboxClass.option,
                  { truncate: !props.noTruncateOptions }
                ]"
              >
                {{ props.placeholder }}
              </ComboboxOption>
              <ComboboxOption
                v-for="option in props.withAutocomplete ? autocomplete.filteredOptions.value : props.options"
                v-slot="{ active, selected }"
                :key="option.value"
                :value="option.value"
                :disabled="option.disabled"
              >
                <div
                  :class="[
                    'cursor-default px-2 py-1 hover:bg-zinc-100',
                    props.comboboxClass.option,
                    {
                      'text-zinc-500': selected,
                      'text-zinc-300': option.disabled,
                      'bg-zinc-100': active,
                      truncate: !props.noTruncateOptions
                    }
                  ]"
                >
                  <span
                    class="mr-2 inline-block h-2 w-2 rounded-full"
                    v-if="option.status"
                    :class="option.status === 'active' ? 'bg-green-600' : 'bg-orange-500'"
                  />
                  {{ option.label }}
                </div>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </Combobox>
      </Field>

      <Field v-else v-model="selectedValues" :name="props.name">
        <Combobox
          class="relative"
          v-model="selectedValues"
          :id="props.withAutocomplete ? undefined : props.id"
          immediate
          multiple
          as="div"
          :disabled="props.disabled"
        >
          <!-- Listbox behavior for multi select -->
          <template v-if="!props.withAutocomplete">
            <ComboboxButton
              class="form-select w-full min-w-0"
              as="button"
              :class="[
                'group form-select w-full min-w-0',
                props.comboboxClass.button,
                {
                  error: !!props.error,
                  '!bg-white !opacity-100': props.readonly
                }
              ]"
              :disabled="props.disabled || props.readonly"
            >
              <div class="min-h-[1.5rem] text-zinc-400" v-if="!selectedValues.length">{{ props.placeholder }}</div>
              <div class="-mx-1 -my-0.5 flex flex-wrap" v-else>
                <div class="flex w-full flex-wrap items-center gap-1" v-if="showAllSelectedValues">
                  <div
                    class="min-w-0"
                    v-for="value in selectedValues"
                    :class="valueTagClass"
                    :style="valueTagStyle"
                    :key="value"
                    @click.stop="selectedValues = selectedValues.filter(selectedValue => selectedValue !== value)"
                  >
                    <span class="min-w-0 truncate">{{ props.options.find(option => option.value === value)?.label ?? value }}</span>
                    <Icon class="flex-shrink-0 text-white" name="xmark" size="sm" />
                  </div>
                  <FormAddHint v-if="props.showAddHint" />
                </div>
                <div class="flex w-full min-w-0 items-center gap-2" v-else>
                  <div
                    class="min-w-0"
                    :class="valueTagClass"
                    :style="valueTagStyle"
                    :key="selectedValues[0]"
                    @click.stop="selectedValues = selectedValues.filter(selectedValue => selectedValue !== selectedValues[0])"
                  >
                    <span class="min-w-0 truncate">{{
                      props.options.find(option => option.value === selectedValues[0])?.label ?? selectedValues[0]
                    }}</span>
                    <Icon class="flex-shrink-0 text-white" name="xmark" size="sm" />
                  </div>
                  <div class="flex flex-shrink-0 items-center space-x-1 text-tarawera-900" v-if="selectedValues.length > 1">
                    <span>+{{ selectedValues.length - 1 }}</span>
                    <Icon
                      :class="theme.primary ? '' : 'text-viking-400'"
                      :style="theme.primary ? { color: theme.primary } : undefined"
                      name="check-square"
                    />
                  </div>
                  <FormAddHint v-else-if="props.showAddHint" />
                </div>
              </div>
            </ComboboxButton>
          </template>
          <!-- Combobox behavior for multiple select -->
          <template v-else>
            <div class="relative w-full">
              <div
                class="group form-select flex min-h-[1.5rem] w-full min-w-0 flex-wrap items-center gap-1 rounded-md pr-10"
                :class="{
                  error: !!props.error,
                  'cursor-not-allowed opacity-50': props.disabled
                }"
                :disabled="props.disabled"
              >
                <div class="relative z-[9] flex min-w-0 flex-wrap gap-1 overflow-hidden" v-if="selectedValues.length">
                  <div
                    v-for="value in selectedValues"
                    :class="valueTagClass"
                    :style="valueTagStyle"
                    :key="value"
                    @click.stop="selectedValues = selectedValues.filter(selectedValue => selectedValue !== value)"
                  >
                    <span class="truncate">{{ props.options.find(option => option.value === value)?.label ?? value }}</span>
                    <Icon class="flex-shrink-0 text-white" name="xmark" size="sm" />
                  </div>
                </div>
                <FormAddHint v-if="props.showAddHint && selectedValues.length && !autocomplete.focused.value" />
                <ComboboxInput
                  class="form-input min-w-0 flex-1 border-none bg-transparent p-0 focus:ring-0"
                  :id="props.id"
                  :ref="autocomplete.inputRef"
                  :disabled="props.disabled"
                  :display-value="() => autocomplete.query.value"
                  :placeholder="selectedValues.length ? '' : props.placeholder"
                  @input="autocomplete.handleInput"
                  @focus="autocomplete.focused.value = true"
                  @blur="autocomplete.focused.value = false"
                  @keydown.backspace.capture="autocomplete.handleBackspaceKey"
                  @keydown.enter.capture="autocomplete.handleEnterKey"
                  @keydown.tab.capture.prevent="autocomplete.handleEnterKey"
                />
              </div>
              <ComboboxButton class="absolute inset-y-0 right-0 z-0 flex w-full items-center text-zinc-500" as="button" :disabled="props.disabled" />
            </div>
          </template>
          <!-- Options for multi select -->
          <ComboboxOptions
            :class="[
              'absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow outline-none focus:border-viking-400 focus-visible:ring-2 focus-visible:ring-viking-400/50',
              props.comboboxClass.options
            ]"
            :ref="autocomplete.optionsRef"
          >
            <slot />
            <template v-if="props.options">
              <ComboboxOption
                v-for="option in props.withAutocomplete ? autocomplete.filteredOptions.value : props.options"
                v-slot="{ active, selected }"
                :key="option.value"
                :value="option.value"
                :disabled="option.disabled || (maxlength && selectedValues.length >= parseInt(maxlength) && !selected)"
              >
                <div
                  :class="[
                    'cursor-default px-2 py-1 hover:bg-zinc-100',
                    props.comboboxClass.option,
                    {
                      'text-zinc-500': selected,
                      'text-zinc-300': option.disabled || (maxlength && selectedValues.length >= parseInt(maxlength) && !selected),
                      'bg-zinc-100': active,
                      truncate: !props.noTruncateOptions
                    }
                  ]"
                >
                  {{ maxlength && selectedValues.length >= parseInt(props.maxlength) && !selected ? option.label + ' (max atteint)' : option.label }}
                </div>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </Combobox>
      </Field>
    </template>

    <template v-else-if="props.type === 'switch'">
      <Field v-model="switchValue" :name="props.name">
        <SwitchGroup class="flex items-center space-x-2 self-center" as="div">
          <SwitchLabel>
            <span v-if="props.switchLabelLeft" v-html="props.switchLabelLeft" />
            <slot name="labelLeft"></slot>
          </SwitchLabel>
          <Switch
            class="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full outline-none duration-300 focus-visible:ring-2 focus-visible:ring-viking-400/50"
            v-model="switchValue"
            :id="props.id"
            :disabled="disabled"
            :class="`${props.inputClass} ${switchValue ? 'bg-viking-400' : 'bg-zinc-300'} ${!props.switchLabelLeft ? '!ml-0' : ''}`"
          >
            <span class="inline-block h-4 w-4 rounded-full bg-white duration-300" :class="switchValue ? 'translate-x-5' : 'translate-x-1'" />
          </Switch>
          <SwitchLabel>
            <span v-if="props.label" v-html="props.label"></span>
            <slot v-else name="label"></slot>
          </SwitchLabel>
        </SwitchGroup>
      </Field>
    </template>

    <template v-else-if="props.type === 'checkbox-list'">
      <Field v-model="selectedValues" :name="props.name">
        <label class="mb-1 block font-bold" v-if="props.label" :for="props.id">
          {{ props.label }}<span class="ml-0.5 text-zinc-400" v-if="props.required">*</span>
        </label>
        <div :class="props.checkboxListClass" :id="props.id">
          <div class="flex items-center space-x-2" v-for="option in props.options" :key="option.value">
            <input
              :class="['form-checkbox', { 'opacity-50': option.disabled }]"
              type="checkbox"
              :id="`${props.name}-${option.value}`"
              :value="option.value"
              :checked="selectedValues.includes(option.value)"
              :disabled="option.disabled"
              :readonly="props.readonly"
              @change="handleCheckboxListChange(option.value, $event.target.checked)"
            />
            <label class="cursor-pointer" :for="`${props.name}-${option.value}`" :class="{ 'text-zinc-400': option.disabled }">
              {{ option.label }}
            </label>
          </div>
        </div>
      </Field>
    </template>

    <template v-else-if="props.type === 'image'">
      <Field :name="props.name">
        <label v-if="!imageUrl" :for="props.id">
          <div
            class="form-image-picker -full relative mx-auto flex aspect-square cursor-pointer flex-col flex-wrap items-center justify-center border bg-zinc-300 duration-300"
            :class="[
              props.imageClass,
              {
                error: !!props.error,
                'hover:bg-viking-400': !theme.primary
              }
            ]"
            :style="theme.primary ? { '--image-hover': theme.primary } : undefined"
          >
            <Icon class="-mt-2" name="camera" size="2x" />
            <p class="mt-1 px-2 text-center text-xs font-bold leading-tight" v-if="props.label">{{ props.label }}</p>
          </div>
        </label>
        <div
          class="relative mx-auto aspect-square w-full bg-zinc-300 bg-cover bg-center"
          v-else
          :class="props.imageClass"
          :style="{
            backgroundImage: `url(${imageUrl})`
          }"
        >
          <button
            class="absolute bottom-1 right-1 flex h-8 w-8 items-center justify-center rounded-full text-white shadow duration-300"
            type="button"
            :class="theme.primary ? 'hover:brightness-95' : 'bg-viking-400 hover:bg-viking-500'"
            :style="theme.primary ? { backgroundColor: theme.primary } : undefined"
            @click="clearImageFile"
          >
            <Icon name="xmark" />
          </button>
        </div>
        <input
          class="hidden"
          :accept="props.accept"
          :id="props.id"
          ref="imageInput"
          :required="props.required"
          type="file"
          @change="handleImageChange"
        />
      </Field>
    </template>

    <slot v-else>
      <Wrapper
        :class="['flex items-center space-x-2', { relative: props.type === 'password' }]"
        :as="['checkbox', 'radio'].includes(props.type) ? 'div' : null"
      >
        <Field
          class="block"
          v-bind="props.type === 'file' ? {} : { value: props.value, 'unchecked-value': props.uncheckedValue }"
          :accept="props.accept"
          :as="props.rows ? 'textarea' : 'input'"
          :autocomplete="props.autocomplete"
          :class="[
            {
              'form-checkbox': props.type === 'checkbox',
              'form-input': !['checkbox', 'file', 'radio'].includes(props.type),
              'form-file': props.type === 'file',
              'form-radio': props.type === 'radio',
              error: !!props.error,
              'w-full min-w-0': !['checkbox', 'radio'].includes(props.type),
              'border-zinc-300 bg-zinc-100 !ring-0': props.readonly,
              'pointer-events-none bg-white': props.readonly && props.type === 'file'
            },
            props.inputClass
          ]"
          :disabled="props.disabled"
          :id="props.id"
          :multiple="props.multiple"
          :maxlength="props.maxlength"
          :max="props.max"
          :min="props.min"
          :name="props.name"
          :placeholder="props.placeholder"
          :readonly="props.readonly"
          :required="props.required"
          :rows="props.rows"
          :type="props.type === 'password' ? (showPassword ? 'text' : 'password') : props.type"
          @input="
            event => {
              emit('change', event.target.value)
              field.handleChange(event)
              if (props.autoResize) resizeTextarea()
            }
          "
          :ref="props.autoResize && props.rows ? textareaRef : null"
          @focus="props.autoResize ? resizeTextarea : null"
          @keyup="props.autoResize ? resizeTextarea : null"
        />
        <div class="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5" v-if="props.type === 'password'">
          <Icon name="eye" @click="showPassword = true" :class="['m-px text-gray-500', showPassword ? '!hidden' : 'block']" />
          <Icon name="eye-slash" @click="showPassword = false" :class="['text-gray-500', showPassword ? 'block' : '!hidden']" />
        </div>
        <template v-if="['checkbox', 'radio'].includes(props.type)">
          <label v-if="props.label" :for="props.id">{{ props.label }}</label>
          <slot v-else name="label"></slot>
        </template>
      </Wrapper>
    </slot>
    <ErrorMessage class="mt-1 inline-block text-sm text-red-500" v-if="!!props.error" :name="props.name" />
  </component>
</template>

<style scoped>
  .form-image-picker:hover {
    background-color: var(--image-hover, #58d9db);
  }
</style>
